import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type Subscription, type Tier } from '../api/endpoints';
import { ApiException } from '../api/client';
import { Skeleton, ErrorState } from '../components/states';
import { useAuth } from '../store/auth';

const PENDING_KEY = 'recipt-billing-pending';
const LABEL: Record<Tier, string> = { free: 'Free', pro: 'Pro', family: 'Family' };

type Status = 'checking' | 'success' | 'pending' | 'none' | 'error' | 'unauth';

export function BillingReturnScreen() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('checking');
  const [sub, setSub] = useState<Subscription | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const run = useCallback(async () => {
    setStatus('checking');
    setErrorMsg(null);
    if (!useAuth.getState().accessToken) {
      setStatus('unauth');
      return;
    }
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) {
      try {
        setSub(await endpoints.getSubscription());
        setStatus('none');
      } catch (e) {
        setErrorMsg(e instanceof ApiException ? e.body.message : 'Не удалось загрузить тариф');
        setStatus('error');
      }
      return;
    }
    let paymentId: string;
    try {
      paymentId = (JSON.parse(raw) as { paymentId: string }).paymentId;
    } catch {
      localStorage.removeItem(PENDING_KEY);
      setStatus('none');
      return;
    }
    try {
      const s = await endpoints.claim(paymentId);
      setSub(s);
      if (s.active && s.tier !== 'free') {
        localStorage.removeItem(PENDING_KEY);
        setStatus('success');
      } else {
        setStatus('pending'); // оплата ещё не подтверждена — pending не чистим (вебхук/повтор добьёт)
      }
    } catch (e) {
      setErrorMsg(e instanceof ApiException ? e.body.message : 'Не удалось подтвердить оплату');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    // Намеренный fetch-in-effect (план без TanStack): разовый setState на маунте — приемлемо.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void run();
  }, [run]);

  return (
    <div className="mx-auto min-h-screen max-w-md bg-cream px-4 pb-10 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <h1 className="text-xl font-bold text-ink">Оплата</h1>

      {status === 'checking' && (
        <div className="mt-6 space-y-3">
          <p className="text-sm text-ink/60">Проверяем оплату…</p>
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {status === 'unauth' && (
        <div className="mt-6 rounded-2xl bg-white p-5 shadow-card">
          <p className="text-lg font-semibold text-ink">Оплата получена ✓</p>
          <p className="mt-1 text-sm text-ink/60">
            Вернитесь в Telegram и откройте приложение — подписка активируется автоматически.
          </p>
        </div>
      )}

      {status === 'error' && (
        <div className="mt-6">
          <ErrorState message={errorMsg ?? 'Ошибка'} onRetry={() => void run()} />
        </div>
      )}

      {status === 'success' && sub && (
        <div className="mt-6 rounded-2xl bg-white p-5 shadow-card">
          <p className="text-lg font-semibold text-ink">Подписка {LABEL[sub.tier]} активна</p>
          {sub.currentPeriodEnd && (
            <p className="mt-1 text-sm text-ink/60">
              действует до {new Date(sub.currentPeriodEnd).toLocaleDateString('ru-RU')}
            </p>
          )}
          <button
            onClick={() => navigate('/')}
            className="mt-4 w-full rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-card transition active:scale-[0.98]"
          >
            В приложение
          </button>
        </div>
      )}

      {status === 'pending' && (
        <div className="mt-6 rounded-2xl bg-white p-5 shadow-card">
          <p className="text-lg font-semibold text-ink">Оплата ещё не подтверждена</p>
          <p className="mt-1 text-sm text-ink/60">Если вы только что оплатили — обновите через пару секунд.</p>
          <div className="mt-4 flex gap-3">
            <button
              onClick={() => void run()}
              className="flex-1 rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-card transition active:scale-[0.98]"
            >
              Обновить
            </button>
            <button onClick={() => navigate('/')} className="rounded-xl border border-ink/15 px-4 py-3 font-semibold text-ink/70">
              В приложение
            </button>
          </div>
        </div>
      )}

      {status === 'none' && sub && (
        <div className="mt-6 rounded-2xl bg-white p-5 shadow-card">
          <p className="text-lg font-semibold text-ink">Ваш тариф: {LABEL[sub.tier]}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 w-full rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-card transition active:scale-[0.98]"
          >
            В приложение
          </button>
        </div>
      )}
    </div>
  );
}
